import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { concat, of, from, Subscription, Subject, Observable } from 'rxjs';
import { map, switchMap, concatMap, filter, debounceTime, distinctUntilChanged, concatAll, finalize, tap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { round1, round2 } from 'src/app/shared/round';

import { Document, DocItems, DocInheritanceRule } from 'src/app/shared/document';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'

import { ProductApiService, StockResult } from 'src/app/services/product-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { RestApiService } from 'src/app/services/rest-api.service';

@Component({
  selector: 'app-new-document-dlg-process-tran-imp',
  templateUrl: './new-document-dlg-process-tran-imp.component.html',
  styleUrls: ['./new-document-dlg-process-tran-imp.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class NewDocumentDlgProcessTranImpComponent implements OnInit, OnChanges, OnDestroy {

  private globalSettinsData: GlobalSettingsData;

  /*
   * Consider that parentDocument may have or may NOT have items property filled with [actual] docItems
   */
  @Input() parentDocument: Document;

  /*
   * Therefore, to keep source object untouched (immutable ????)
   * we will expect alone/independent copy of DocItems as Input parameter
   * 
   */

  @Input() docItems: DocItems[] = [];

  @Input() newDocument: Document = null;

  @Input() newDocuments: Document[] = [];

  public get hasParentDocument(): boolean { return (this.parentDocumentId && true); }

  @Input() command: number = 0;   /* Command to child components what to do
                                   *
                                   * 0 - nothing / prepare
                                   * 1 - go
                                   * 2 - clean
                                   * 3 - reset to initial state
                                   * 
                                   */
  parentDocumentId: number = 0;

  sourceDocItems: DocItems[] = null;

  blocked: boolean = false;

  stock1: number = 0;
  stock2: number = 0;
  stock3: number = 0;

  allowImpStock: boolean = false;
  allowAuxStock: boolean = false;
  allowCmpStock: boolean = false;

  @Input() selectedActions: any = [1, 3];

  private newDocumentProcess$ = new Subject<number>();
  private newDocumentProcessQuery: Subscription = null;

  private a1MoveOptions$ = new Subject<{ o1: boolean, s1: number, o2: boolean, s2: number, o3: boolean, s3: number, }>();
  private _move1ActionSubs: Subscription = null;

  private createNewDocument$ = new Subject<Document>();
  private _createNewDoc: Subscription = null;

  @Output() onCreateNewDocuments = new EventEmitter<Document[]>();

  constructor(
    private restApi: RestApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private cliApi: ClientApiService,
    private prodApi: ProductApiService,
  ) {

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.createMoveSubscriptions();
    this.createNewDocumentSubscription();
    this.createNewDocumentProcessSubscription();

    this.setInitialValues();
  }

  /**
   * Creates subscription chain that:
   * 1. Listens to createNewDocument$ Subject emissions (each emission is a template Document to be populated)
   * 2. Marks UI as blocked while processing
   * 3. Resolves (maps) proper child departments for client (destination) and manufacturer (source)
   * 4. Iterates over newDocument.items (the original template items) to:
   *    a. Skip zero-quantity lines early
   *    b. Check destination (target) stock to reduce required quantity by what is already available
   *    c. Skip if no shortage remains
   *    d. Check source stock to determine how much can actually be moved (cap by available source stock)
   *    e. Skip if nothing available on source
   *    f. Resolve income price (vendor / manufacturing incoming price)
   *    g. Calculate VAT / price / totals and push a cloned/enriched item into the working document's items array
   * 5. When all items processed:
   *    - Push completed document (only if it contains items) into newDocuments array
   *    - Emit aggregated newDocuments to parent via onCreateNewDocuments
   *    - Unblock UI
   *
   * Stream structure (per document emission):
   * createNewDocument$ (Document)
   *  -> concatMap (block UI)
   *  -> concatMap (resolve client child department)
   *  -> concatMap (resolve manufacturer child department)
   *  -> concat(
   *        from(items)
   *          |> filter / transform chain per item
   *        ,
   *        of(document) finalization branch
   *     )
   *
   * Notes:
   * - concatMap is used to preserve order and ensure sequential async resolution
   * - Each product line passes through multiple remote calls; early filters reduce unnecessary traffic
   * - Quantity adjustments mutate the working item copies only (original template untouched)
   * - All console.log retained (can be guarded by environment.production if desired)
   */
  createNewDocumentSubscription(): void {
    this._createNewDoc =
      this.createNewDocument$
        .pipe(
          // Mark component as busy and forward the document
          concatMap(ndi => { 
            this.blocked = true; 
            return of(ndi); 
          }),

          // Resolve and set destination (client) child department ID (DOCPCODEPID)
          concatMap(ndi => 
            this.cliApi.getChildDepartmentsById(ndi.DOCCLIDEPID, ndi.DOCPCOCLIID).pipe(
              map(d => {
                // Expecting array; take first element's CLIID
                ndi.DOCPCODEPID = d[0]?.CLIID;
                return ndi;
              })
            )
          ),

            // Resolve and set source (manufacturer) child department ID (DOCLCODEPID)
          concatMap(ndi => 
            this.cliApi.getChildDepartmentsById(ndi.DOCMANDEPID, ndi.DOCLCOCLIID).pipe(
              map(d => {
                ndi.DOCLCODEPID = d[0]?.CLIID;
                return ndi;
              })
            )
          ),

          // Process each initial item + finalize document
          concatMap(ndi => concat(
            from(this.newDocument.items).pipe(

              // 1. Need only lines with requested quantity
              filter(di => (di.DOCITEMQTY > 0)),

              // filter by new documents content created on previouse steps

              filter(di => {

                let result = true;

                if (this.newDocuments && this.newDocuments.length) {
                  for (let nd of this.newDocuments) {
                    if (nd.items && nd.items.length) {
                      for (let ndi of nd.items) {
                        if (ndi.PRODID === di.PRODID) {
                          result = false;

                          // TODO: Here also could be special case when quantity from previouse document not enough
                          
                          break;
                        }
                      }
                    }
                    if (!result) { break; }
                  }
                }
                return result;
              }),

              // 2. Check target (destination) stock prior to move: reduce shortage
              concatMap(di =>
                this.prodApi.getProductStockBefore(
                  di.PRODID,
                  ndi.DOCCLIENTSID,
                  ndi.DOCCLIDEPID,
                  this.parentDocument.DOCDATE
                ).pipe(
                  map(stock => {
                    di.DOCITEMGLOBALSTOCK = Math.max((stock as StockResult[])[0]?.stock ?? 0, 0);

                    // Reduce requested quantity by what destination already has
                    if (di.DOCITEMQTY > di.DOCITEMGLOBALSTOCK) {
                      di.DOCITEMQTY = di.DOCITEMQTY - di.DOCITEMGLOBALSTOCK;
                    } else {
                      di.DOCITEMQTY = 0; // nothing to move
                    }
                    return di;
                  })
                )
              ),

              // 3. Skip if no shortage after destination stock adjustment
              filter(di => (di.DOCITEMQTY > 0)),

              // 4. Check source stock availability; cap quantity to what source can provide
              concatMap(di =>
                this.prodApi.getProductStockBefore(
                  di.PRODID,
                  ndi.DOCMANCLIID,
                  ndi.DOCMANDEPID,
                  this.parentDocument.DOCDATE
                ).pipe(
                  map(stock => {
                    di.DOCITEMGLOBALSTOCK = Math.max((stock as StockResult[])[0]?.stock ?? 0, 0);

                    if (di.DOCITEMQTY > di.DOCITEMGLOBALSTOCK) {
                      di.DOCITEMQTY = di.DOCITEMGLOBALSTOCK; // cap by source availability
                    }
                    return di;
                  })
                )
              ),

              // 5. Skip if source has nothing deliverable
              filter(di => (di.DOCITEMQTY > 0)),

              // 6. Resolve income price needed for pricing calculations
              concatMap(di2 =>
                this.prodApi.getProdIncomePriceById(
                  di2.PRODID,
                  ndi.DOCMANCLIID,
                  ndi.DOCMANDEPID,
                  ndi.DOCCURRID,
                  di2.DOCITEMQTY,
                  ndi.DOCDATE
                ).pipe(
                  map(price => {
                    di2.DOCITEMVPRICE = price[0]?.PRODINCOMEPRICE;

                    // Ensure VAT is set
                    di2.PRODVAT = (di2.PRODVAT ?? this.globalSettinsData.vatValue);

                    // Calculate pricing (DOCITEMPRICE is VAT inclusive)
                    di2.DOCITEMPRICE = di2.DOCITEMVPRICE * ndi.DOCRATEVAL;
                    di2.DOCITEMPRICEWOVAT = round2(di2.DOCITEMPRICE / ((100.0 + di2.PRODVAT) / 100.0));
                    di2.DOCITEMSUM = di2.DOCITEMPRICE * di2.DOCITEMQTY;

                    // Push cloned enriched line into working document
                    ndi.items.push({ ...di2 });

                    return di2;
                  })
                )
              ),

            ),

            // Finalization branch: emit document only if it has at least one item
            of(ndi).pipe(
              map(doc => {
                if (doc.items.length) {
                  this.newDocuments.push(doc);
                  this.onCreateNewDocuments.emit(this.newDocuments);
                }
                this.blocked = false;
                return doc;
              })
            )
          )),
        )
        .subscribe({
          error: (err) => {
            // In case of an error, unblock UI (optional safeguard)
            this.blocked = false;
            console.error('createNewDocumentSubscription error:', err);
          }
        });
  }

  /**
    * createMoveSubscriptions
    * -----------------------
    * Pseudocode (detailed plan / reasoning):
    * 1. Subscribe to a1MoveOptions$ (Subject emitting move options object { o1,o2,o3,s1,s2,s3 }).
    * 2. For every emission:
    *    a. Reset aggregated newDocuments array (fresh build per option change).
    *    b. Prepare base (original) document date (od) taken from parentDocument.DOCDATE.
    *    c. Maintain a backward-seconds counter (bm) to ensure each generated document has a unique timestamp.
    *    d. For each enabled option (o1, o2, o3):
    *        - Compute a distinct date (bd) = od - bm seconds; increment bm.
    *        - Emit a new template document through createNewDocument$:
    *              • Spread current this.newDocument as a base template (immutable cloning).
    *              • Override date fields (DOCDATE, DOCEXPDATE, DOCVALDATE) with bd.
    *              • Set destination department (DOCCLIDEPID) to main stock (from global settings).
    *              • Set source department (DOCMANDEPID) to the selected stock (s1/s2/s3).
    *              • Provide a DOCNOTE2 trace text.
    *              • Initialize items: [] so that createNewDocumentSubscription can fill it.
    *    e. Return a passthrough observable (of(o)) to complete the concatMap chain.
    * -----------------------
    * Pseudocode (updated with error handling):
    * 1. Listen to a1MoveOptions$ emissions.
    * 2. For each emission (o):
    *    - Reset newDocuments.
    *    - Derive base date (od) from parentDocument.DOCDATE.
    *    - Use bm to shift seconds backwards for ordering.
    *    - For each enabled option (o1/o2/o3) build a template document and emit via createNewDocument$.
    * 3. Return of(o) to satisfy concatMap (value ignored).
    * 4. Subscribe with:
    *    - next: no-op (side effects already done inside concatMap).
    *    - error: log error and (safeguard) unblock UI if it was blocked elsewhere.
    *    - complete: optional diagnostic log.
    */
   createMoveSubscriptions() {
     this._move1ActionSubs =
       this.a1MoveOptions$
         .pipe(
           concatMap(o => {
             this.newDocuments = [];

             console.log('*** NewDocumentDlgProcessTranImpComponent in a1MoveOptions$ D1');

             let bm: number = 5; // seconds offset to preserve ordering
             const od: Date = new Date(this.parentDocument.DOCDATE);

             if (o.o1) {
               const bd = new Date(od.getFullYear(), od.getMonth(), od.getDate(), od.getHours(), od.getMinutes(), od.getSeconds() - bm++);
               this.createNewDocument$.next({
                 ...this.newDocument,
                 DOCDATE: bd,
                 DOCEXPDATE: bd,
                 DOCVALDATE: bd,
                 DOCCLIDEPID: this.globalsettingsservice.getGlobalSettings().mainStockId,
                 DOCMANDEPID: o.s1,
                 DOCNOTE2: 'Передача со склада ' + o.s1,
                 items: []
               });
             }

             if (o.o2) {
               const bd = new Date(od.getFullYear(), od.getMonth(), od.getDate(), od.getHours(), od.getMinutes(), od.getSeconds() - bm++);
               this.createNewDocument$.next({
                 ...this.newDocument,
                 DOCDATE: bd,
                 DOCEXPDATE: bd,
                 DOCVALDATE: bd,
                 DOCCLIDEPID: this.globalsettingsservice.getGlobalSettings().mainStockId,
                 DOCMANDEPID: o.s2,
                 DOCNOTE2: 'Передача со склада ' + o.s2,
                 items: []
               });
             }

             if (o.o3) {
               const bd = new Date(od.getFullYear(), od.getMonth(), od.getDate(), od.getHours(), od.getMinutes(), od.getSeconds() - bm++);
               this.createNewDocument$.next({
                 ...this.newDocument,
                 DOCDATE: bd,
                 DOCEXPDATE: bd,
                 DOCVALDATE: bd,
                 DOCCLIDEPID: this.globalsettingsservice.getGlobalSettings().mainStockId,
                 DOCMANDEPID: o.s3,
                 DOCNOTE2: 'Передача со склада ' + o.s3,
                 items: []
               });
             }

             return of(o); // fire-and-forget
           })
         )
         .subscribe({
           next: () => {
             // No-op: side effects executed in concatMap
           },
           error: (err) => {
             console.error('createMoveSubscriptions error:', err);
             this.blocked = false; // safeguard
           },
           complete: () => {
             console.log('createMoveSubscriptions completed');
           }
         });
   }

  /*
   *
   * Action selected either during initial call depending on parentDocument type
   * or
   * By selecting Corresponding tab
   *
   * Actually, we could only select between specialized type of move (different for invoice and shipping document for example)
   * and general move - with freely selected stocks
   *
   * Below subscription prepares the view default backend values
   * In general we shouldn't change anything manually
   *
   */
  createNewDocumentProcessSubscription() {

    let rowSortIndex: number;

    if (this.newDocumentProcessQuery) { this.newDocumentProcessQuery.unsubscribe(); }

    this.newDocumentProcessQuery =
        this.newDocumentProcess$
        .pipe(
          debounceTime(300),
          tap(options => environment.production || console.log('*** NewDocumentDlgProcessTranImpComponent in newDocumentProcess$: ' + JSON.stringify(options))),

          switchMap(options => { this.blocked = true; return of(options); }),
          tap(options => environment.production || console.log('*** NewDocumentDlgProcessTranImpComponent in newDocumentProcess$: ' + JSON.stringify(options))),

          concatMap(options => this.restApi.getCurrencyInfoByID(options).pipe(

            tap(rate => environment.production || console.log('*** NewDocumentDlgProcessTranImpComponent in getCurrencyInfoByID: ' + JSON.stringify(rate))),

            concatMap( rate => {
              this.newDocument.DOCRATEVAL = rate[0].CURRATE;
              this.newDocument.items = [];
              rowSortIndex = 10;

              return from((this.sourceDocItems || []));
            }),

            concatMap(di => {
              //environment.production || console.table(di);
              environment.production || console.log('*** NewDocumentDlgProcessTranImpComponent in createNewDocumentProcessSubscription: ndi ' + di.PRODCODE + ' Q: ' + di.DOCITEMQTY);

              let ndi: DocItems = {
                ...di,
                ... {
                  DOCITEMID: -2,
                  cloneIndex: 0,
                  U4ETKA: null,
                  DOCITEMCURRID: this.newDocument.DOCCURRID,
                  DOCITEMCRATE: this.newDocument.DOCRATEVAL,
                  PAYITEMCLIID: rowSortIndex,
                  PRODVAT: di.PRODVAT ?? this.globalSettinsData.vatValue,
                }
              };

              rowSortIndex += 10;

              let kVat = ((100.0 + ndi.PRODVAT) / 100.0); // <= 1,2 when VAT is 20%

              ndi.DOCITEMPRICEWOVAT = round1((di.DOCITEMVPRICE * this.newDocument.DOCRATEVAL) / kVat);
              ndi.DOCITEMPRICE = round2(ndi.DOCITEMPRICEWOVAT * kVat);
              ndi.DOCITEMVPRICE = di.DOCITEMPRICE / this.newDocument.DOCRATEVAL;

              if (this.selectedActions.includes(1)) {

                ndi.DOCITEMVPRICE = di.DOCITEMVPRICE;

                ndi.DOCITEMPRICEWOVAT = round1((ndi.DOCITEMVPRICE * this.newDocument.DOCRATEVAL) / kVat);
                ndi.DOCITEMPRICE = round2(ndi.DOCITEMPRICEWOVAT * kVat);
              }

              ndi.DOCITEMSUM = ndi.DOCITEMPRICE * ndi.DOCITEMQTY;

              return of(ndi);
            }),
            /*
             * Make some pre-flight check to avoid unneccesary further processes
             *
             * 1) Check is there already items on the stock
             * 2) Check is there item already shipped
             * 3) Something else
             */
            concatMap(ndi => this.prodApi.getProductShippedByDoc(ndi.PRODID, this.parentDocument.DOCID, new Date()).pipe(

              filter(shipped => (shipped && shipped.length > 0)),

              map(shipped => {
                environment.production || console.table(shipped);
                ndi.DOCITEMSHIPPED = shipped[0].DOCSHIPPEDITEMS;

                environment.production || console.log('*** NewDocumentDlgProcessTranImpComponent in _preFlightCheck: process ' + ndi.PRODCODE + ' Q: ' + ndi.DOCITEMQTY + ' - Shipped Items: ' + shipped[0].DOCSHIPPEDITEMS);

                return ndi;
              })
            )),

            filter(ndi => (ndi.DOCITEMQTY > 0)),  // Continue only if we really need this product on main stock

            concatMap(ndi => this.prodApi.getProdInfoById(
              ndi.PRODID,
              this.newDocument.DOCMANCLIID,
              this.newDocument.DOCCURRID,
              //
              // It depends on document type ????
              //
              this.globalSettinsData.mainStockId,
              this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
              this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
              //
              // Is it on actual date or document date
              // Could depends on....
              //
              new Date()
            )
              .pipe(map(data => {
                ndi.prodInfo = data[0];

                environment.production || console.log('*** NewDocumentDlgProcessTranImpComponent in getProdInfoById: process ' + ndi.PRODCODE + ' Q: ' + ndi.DOCITEMQTY + ' - ProdInfo: ' + JSON.stringify(data));
                //
                //  Finally, after all succesfull checking we could push item to newDocument Items array,
                //
                this.newDocument.items.push(ndi);

                return ndi;
              }))),

            finalize(() => {
              environment.production || console.log('*** NewDocumentDlgProcessTranImpComponent in newDocumentProcess$ - Finalize');

              this.a1MoveOptions$.next({
                o1: this.allowImpStock,
                o2: this.allowAuxStock,
                o3: this.allowCmpStock,
                s1: this.stock1,
                s2: this.stock2,
                s3: this.stock3,
              });
            }),
            ),
          ),
        )
        .subscribe(
          err => {
            // some error happened
            console.log('*** NewDocumentDlgProcessTranImpComponentin newDocumentProcess$ - Finalize Err: ' + err);
          });
  }

  setInitialValues() {
    console.log('*** NewDocumentDlgProcessTranImpComponent setInitialValues');

    this.stock1 = this.globalSettinsData.impStockId;
    this.stock2 = this.globalSettinsData.auxStockId;
    this.stock3 = this.globalSettinsData.auxDepartmentId;

    this.allowImpStock = this.globalSettinsData.impCountFlag;
    this.allowAuxStock = this.globalSettinsData.auxCountFlag;
    this.allowCmpStock = false;
  }

  processDocItems() {
    console.log('*** NewDocumentDlgProcessTranImpComponent in processDocItems');
    /*
     * Here we come with newDocument prepared in createNewDocument initial procedure
     *
     * Furthe we have to process document content
     * to prepare the inital template for further processing
     *
     * Target stock already set
     * 
     * Get Actual currency rate for newDocument
     * And process all items when Currency Rate become available
     * 
     * Those one in newDocument.DOCRATEVAL from createNewDocument Dialog would arrive later
     */
    this.newDocumentProcess$.next(this.newDocument.DOCCURRID);
    // this.newDocumentProcess$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** NewDocumentDlgProcessTranImpComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));

      switch (propName) {

        case 'command':
          switch (change.currentValue) {
            case 0:
              //this.createMoveSubscriptions();
              //this.createNewDocumentSubscription();
              //this.createNewDocumentProcessSubscription();

              //this.setInitialValues();
              break;

            case 1:
              this.processDocItems();
              break;

            case 2:
              this.newDocuments = [];
              this.onCreateNewDocuments.emit(this.newDocuments);
              break;

            case 3:
              this.setInitialValues();
              break;

          }
          break;

        case 'newDocument':
          if (this.newDocument) {
          }
          break;

        case 'docItems':
          if (this.docItems) {
            this.sourceDocItems = [... this.docItems];
          }
          break;
      }
    }
  }

  ngOnDestroy(): void {
    console.log('*** NewDocumentDlgProcessTranImpComponent in ngOnDestroy')

    if (this.newDocumentProcessQuery) { this.newDocumentProcessQuery.unsubscribe(); }

    if (this._move1ActionSubs) { this._move1ActionSubs.unsubscribe(); }
    if (this._createNewDoc) { this._createNewDoc.unsubscribe(); }
  }

  ngOnInit(): void {
    console.log('*** NewDocumentDlgProcessTranImpComponent in ngOnInit');

    /*
     * TODO: Make subscription to newDocument currency rate, because it is quite complex task to wait the set the rate in the main thread
     * Potentially problematic due to different sources ??????
     */

  //  this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

  //  this.createMoveSubscriptions();
  //  this.createNewDocumentSubscription();
  //  this.createNewDocumentProcessSubscription();

  //  this.setInitialValues();
  }

  onDocTypeChanges(event) {
    console.log('*** NewDocumentDlgProcessTranImpComponent onDocTypeChanges ' + JSON.stringify(event));

    if (event.value.includes(1)) {
      console.log('*** NewDocumentDlgProcessTranImpComponent doCurrencyBased document ' + JSON.stringify(event.value));
      /*
       * TODO: Check whether currency of newDocument equal to those in parentDocument
       *       May be we need more sofisticated parameters checking
       */
    }
  }

  onA1Stock1Selected(event) {
    console.log('*** NewDocumentDlgProcessTranImpComponent onA1Stock1Selected ' + JSON.stringify(event));

    this.stock1 = event;
  }

  onA1Stock2Selected(event) {
    console.log('*** NewDocumentDlgProcessTranImpComponent onA1Stock2Selected ' + JSON.stringify(event));

    this.stock2 = event;
  }

  onA1Stock3Selected(event) {
    console.log('*** NewDocumentDlgProcessTranImpComponent onA1Stock3Selected ' + JSON.stringify(event));

    this.stock3 = event;
  }

  onA1StockSelectionChange(event) {
    console.log('*** NewDocumentDlgProcessTranImpComponent onA1StockSelectionChange ' + JSON.stringify(event));

  }
}
